import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const isResetModeInitial = Boolean(resetToken);
  const [isResetMode, setIsResetMode] = useState(isResetModeInitial);
  const { login } = useAuthStore();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string>('');
  
  // OTP step state
  const [otpToken, setOtpToken] = useState<string>('');
  const [otpCode, setOtpCode] = useState<string>('');
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [otpError, setOtpError] = useState<string>('');
  const [resendTimer, setResendTimer] = useState(0);

  // Phone number step state
  const [showPhoneStep, setShowPhoneStep] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof LoginFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    // Clear login error when user starts typing
    if (loginError) {
      setLoginError('');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginFormData> = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required' as any;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address' as any;
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required' as any;
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters' as any;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setLoginError(''); // Clear any previous errors
    try {
      // Try to login with actual API
      const loginResponse = await authAPI.login(formData.email, formData.password);

      // If OTP is required, switch to OTP step
      if (loginResponse?.requires_otp && loginResponse?.otp_token) {
        setOtpToken(loginResponse.otp_token);
        setShowOtpStep(true);
        setShowPhoneStep(false);
        setOtpCode('');
        setOtpError('');
        setResendTimer(60);
        toast.success('OTP sent! Please check your WhatsApp or email.', { icon: '💬' });
        setIsLoading(false);
        return;
      }

      // If phone number is required, switch to phone step
      if (loginResponse?.requires_phone && loginResponse?.otp_token) {
        setOtpToken(loginResponse.otp_token);
        setShowPhoneStep(true);
        setShowOtpStep(false);
        setPhoneNumber('');
        setPhoneError('');
        setIsLoading(false);
        return;
      }

      if (loginResponse?.access_token) {
        // Clear any old auth data first to prevent showing wrong user
        useAuthStore.getState().logout();
        
        // Set token in auth store first so the request interceptor can use it
        useAuthStore.getState().setToken(loginResponse.access_token);
        
        // Get user details after successful login
        // Pass token directly in config to ensure it's available immediately
        try {
          const userResponse = await authAPI.getCurrentUser(loginResponse.access_token);
          
          // Update auth store with user data
          login(userResponse, loginResponse.access_token);
          
          // Show success toast
          toast.success(`Welcome back, ${userResponse.username}!`, {
            duration: 4000,
            icon: '🎉',
          });
          
          // Navigate to dashboard
          navigate('/dashboard');
        } catch (userError) {
          // If we can't get user details, login is incomplete - show error
          console.error('Failed to get user details after login:', userError);
          setLoginError('Login incomplete. Please try again.');
        }
      } else {
        // Response didn't match expected shapes — log for debugging
        console.error('[Login] Unexpected response shape:', JSON.stringify(loginResponse));
        setLoginError('Login failed. Please try again.');
      }
      
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Show specific error message for invalid credentials
      if (error.response?.status === 401 || error.response?.status === 400) {
        setLoginError('Wrong email address or password!');
      } else if (error.response?.status === 422) {
        setLoginError('Invalid email format. Please check your email address.');
      } else if (error.message === 'Network Error') {
        setLoginError('Network error. Please check your connection and try again.');
      } else {
        setLoginError(`Login failed: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const doVerifyOtp = async (code: string) => {
    if (!code || code.length < 6 || isLoading) return;
    
    setIsLoading(true);
    setOtpError('');
    try {
      const verifyResponse = await authAPI.verifyOtp(otpToken, code);

      if (verifyResponse?.access_token) {
        useAuthStore.getState().logout();
        useAuthStore.getState().setToken(verifyResponse.access_token);

        try {
          const userResponse = await authAPI.getCurrentUser(verifyResponse.access_token);
          login(userResponse, verifyResponse.access_token);
          toast.success(`Welcome back, ${userResponse.username}!`, {
            duration: 4000,
            icon: '🎉',
          });
          // Fresh OTP login — this device wasn't already trusted. Prompt to
          // trust it once inside the app (see App.tsx), rather than here.
          navigate('/dashboard', { state: { promptTrustDevice: true } });
        } catch (userError) {
          console.error('Failed to get user details after OTP:', userError);
          setOtpError('Login incomplete. Please try again.');
        }
      } else {
        console.error('[OTP] No access_token in response:', JSON.stringify(verifyResponse));
        setOtpError('Verification failed. Please try again.');
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);
      if (error.response?.status === 401) {
        setOtpError('Invalid OTP code. Please try again.');
      } else {
        setOtpError(`Verification failed: ${error.message || 'Please try again.'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await doVerifyOtp(otpCode);
  };
  
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setIsLoading(true);
    try {
      await authAPI.sendOtp(otpToken);
      setResendTimer(60);
      toast.success('OTP resent to your WhatsApp!', { icon: '💬' });
    } catch (error: any) {
      console.error('Resend OTP error:', error);
      setOtpError(error.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtpEmail = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await authAPI.sendOtpEmail(otpToken);
      toast.success('OTP sent to your email!', { icon: '✉️' });
    } catch (error: any) {
      console.error('Send OTP by email error:', error);
      setOtpError(error.response?.data?.message || 'Failed to send OTP by email.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSavePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 8) {
      setPhoneError('Please enter a valid phone number with country code (e.g. +263781761173)');
      return;
    }

    setIsLoading(true);
    setPhoneError('');
    try {
      const response = await authAPI.savePhone(otpToken, phoneNumber);

      if (response?.requires_otp && response?.otp_token) {
        setOtpToken(response.otp_token);
        setShowPhoneStep(false);
        setShowOtpStep(true);
        setOtpCode('');
        setOtpError('');
        setResendTimer(60);
        toast.success('Phone saved! OTP sent to your WhatsApp.', { icon: '💬' });
      } else {
        console.error('[SavePhone] Unexpected response:', JSON.stringify(response));
        setPhoneError('Failed to save phone number. Please try again.');
      }
    } catch (error: any) {
      console.error('Save phone error:', error);
      setPhoneError(error.response?.data?.message || 'Failed to save phone number. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Countdown timer for resend
  React.useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  const handleForgotPassword = async (email: string) => {
    try {
      await authAPI.forgotPassword(email);
      toast.success('Password reset email sent! Check your inbox.', {
        duration: 5000,
        icon: '📧',
      });
      setShowForgotPassword(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send reset email. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(56,189,248,0.22),transparent_45%),radial-gradient(circle_at_85%_20%,rgba(167,139,250,0.22),transparent_45%),radial-gradient(circle_at_50%_90%,rgba(34,211,238,0.12),transparent_55%)]" />
      <div className="absolute inset-0 opacity-[0.12] [background:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:48px_48px]" />

      {/* Main container */}
      <div className="relative z-10 w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        
        {/* Left side - Branding & Info */}
        <div className="text-center lg:text-left space-y-8">
          {/* Logo & Brand */}
          <div className="space-y-6">
            <div className="flex justify-center lg:justify-start">
              <div className="relative">
                <img 
                  src="/logo.png" 
                  alt="Aura Group" 
                  className="h-16 w-auto drop-shadow-2xl"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="h-16 w-16 bg-gradient-to-br from-blue-400 to-purple-600 rounded-2xl items-center justify-center hidden drop-shadow-2xl">
                  <i className="fas fa-satellite text-white text-2xl"></i>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h1 className="text-4xl lg:text-6xl font-black text-white leading-tight tracking-tight">
                Manage usage.{' '}
                <span className="bg-gradient-to-r from-sky-200 via-cyan-200 to-violet-200 bg-clip-text text-transparent">
                  Monitor performance.
                </span>
              </h1>
              <p className="text-base lg:text-lg text-white/70 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                A clean, secure dashboard for Starlink service lines—real-time analytics, alerts, exports and operational controls.
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 max-w-lg mx-auto lg:mx-0">
            <div className="flex items-center space-x-3 text-blue-100">
              <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <i className="fas fa-chart-line text-blue-300"></i>
              </div>
              <span className="font-medium">Real-time Analytics</span>
            </div>
            <div className="flex items-center space-x-3 text-purple-100">
              <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <i className="fas fa-shield-alt text-purple-300"></i>
              </div>
              <span className="font-medium">Enterprise Security</span>
            </div>
            <div className="flex items-center space-x-3 text-cyan-100">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <i className="fas fa-satellite text-cyan-300"></i>
              </div>
              <span className="font-medium">Starlink Integration</span>
            </div>
            <div className="flex items-center space-x-3 text-emerald-100">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <i className="fas fa-robot text-emerald-300"></i>
              </div>
              <span className="font-medium">AI Recommendations</span>
            </div>
          </div>
        </div>

        {/* Right side - Login Form */}
        <div className="w-full max-w-md mx-auto">
          <div className="rounded-[28px] bg-gradient-to-r from-sky-500/40 via-violet-500/30 to-cyan-500/40 p-[1px] shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_30px_80px_-40px_rgba(0,0,0,0.8)]">
            <div className="bg-white/95 backdrop-blur-xl rounded-[27px] p-8 lg:p-10">
            {isResetMode ? (
              <ResetPasswordForm
                token={resetToken as string}
                onSuccess={() => {
                  toast.success('Password reset successfully! You may now login.');
                  setIsResetMode(false);
                  navigate('/login');
                }}
              />
            ) : showPhoneStep ? (
              <div>
                {/* Phone Header */}
                <div className="text-center mb-8">
                  <div className="mx-auto mb-5 flex items-center justify-center">
                    <div className="relative group">
                      <div className="absolute -inset-2 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300 animate-pulse bg-green-600/40"></div>
                      <div className="relative w-20 h-20 rounded-3xl flex items-center justify-center shadow-xl ring-4 ring-offset-4 ring-offset-transparent ring-green-600/20 animate-pulse bg-white">
                        <img
                          src="/logo.png"
                          alt="Aura Group"
                          className="w-12 h-12 object-contain drop-shadow"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl items-center justify-center hidden shadow-md">
                          <i className="fab fa-whatsapp text-white text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-sm animate-pulse"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-black text-slate-900 mb-2 tracking-tight">
                    Add WhatsApp Number
                  </h3>
                  <p className="text-slate-500 text-sm mt-2">
                    Enter your WhatsApp phone number to receive OTP codes
                  </p>
                </div>

                {/* Phone Form */}
                <form onSubmit={handleSavePhone} className="space-y-6">
                  <div className="group">
                    <label className="block text-slate-700 text-sm font-semibold mb-2">
                      WhatsApp Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <i className="fab fa-whatsapp text-slate-400 group-focus-within:text-green-600 transition-colors text-lg"></i>
                      </div>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => {
                          setPhoneNumber(e.target.value);
                          if (phoneError) setPhoneError('');
                        }}
                        autoComplete="tel"
                        className="w-full pl-12 pr-4 py-3.5 bg-slate-50 rounded-2xl text-slate-900 placeholder-slate-400 ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500/40 transition-all duration-200 font-medium"
                        placeholder="+263 78 176 1173"
                      />
                    </div>
                    {phoneError && (
                      <p className="text-red-600 text-sm mt-2 flex items-center">
                        <i className="fas fa-exclamation-circle mr-2"></i>
                        {phoneError}
                      </p>
                    )}
                    <p className="text-slate-400 text-xs mt-2">
                      Include your country code (e.g. +263 for Zimbabwe)
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="relative w-full overflow-hidden bg-gradient-to-r from-green-600 via-emerald-600 to-green-700 hover:brightness-110 text-white font-bold py-3.5 px-6 rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl"
                  >
                    <span className="absolute inset-0 opacity-0 transition-opacity duration-300 hover:opacity-100 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_45%)]" />
                    {isLoading ? (
                      <div className="relative flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                        Saving…
                      </div>
                    ) : (
                      <div className="relative flex items-center justify-center">
                        <i className="fab fa-whatsapp mr-3 text-lg"></i>
                        Save &amp; Send OTP
                      </div>
                    )}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPhoneStep(false);
                        setPhoneNumber('');
                        setPhoneError('');
                        setOtpToken('');
                      }}
                      className="text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors"
                    >
                      <i className="fas fa-arrow-left mr-1"></i>
                      Back to login
                    </button>
                  </div>
                </form>
              </div>
            ) : showOtpStep ? (
              <div>
                {/* OTP Header */}
                <div className="text-center mb-8">
                  <div className="mx-auto mb-5 flex items-center justify-center">
                    <div className="relative group">
                      <div className="absolute -inset-2 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300 animate-pulse bg-indigo-600/40"></div>
                      <div className="relative w-20 h-20 rounded-3xl flex items-center justify-center shadow-xl ring-4 ring-offset-4 ring-offset-transparent ring-indigo-600/20 animate-pulse bg-white">
                        <img
                          src="/logo.png"
                          alt="Aura Group"
                          className="w-12 h-12 object-contain drop-shadow"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl items-center justify-center hidden shadow-md">
                          <i className="fas fa-database text-white text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-sm animate-pulse"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-black text-slate-900 mb-2 tracking-tight">
                    Verify OTP
                  </h3>
                  <p className="text-slate-500 text-sm mt-2">
                    Enter the 6-digit code sent to your WhatsApp or email
                  </p>
                </div>

                {/* OTP Form */}
                <form onSubmit={handleOtpSubmit} className="space-y-6">
                  <div className="group">
                    <label className="block text-slate-700 text-sm font-semibold mb-2">
                      OTP Code
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <i className="fas fa-shield-alt text-slate-400 group-focus-within:text-sky-600 transition-colors"></i>
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={otpCode}
                        disabled={isLoading}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtpCode(val);
                          if (otpError) setOtpError('');
                          if (val.length === 6 && !isLoading) {
                            doVerifyOtp(val);
                          }
                        }}
                        autoComplete="one-time-code"
                        className="w-full pl-12 pr-4 py-3.5 bg-slate-50 rounded-2xl text-slate-900 placeholder-slate-400 ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition-all duration-200 text-center tracking-[0.5em] font-mono text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="000000"
                      />
                    </div>
                    {otpError && (
                      <p className="text-red-600 text-sm mt-2 flex items-center">
                        <i className="fas fa-exclamation-circle mr-2"></i>
                        {otpError}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="relative w-full overflow-hidden bg-gradient-to-r from-sky-600 via-violet-600 to-sky-700 hover:brightness-110 text-white font-bold py-3.5 px-6 rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl"
                  >
                    <span className="absolute inset-0 opacity-0 transition-opacity duration-300 hover:opacity-100 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_45%)]" />
                    {isLoading ? (
                      <div className="relative flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                        Verifying…
                      </div>
                    ) : (
                      <div className="relative flex items-center justify-center">
                        <i className="fas fa-check mr-3"></i>
                        Verify
                      </div>
                    )}
                  </button>

                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => {
                          setShowOtpStep(false);
                          setShowPhoneStep(false);
                          setOtpCode('');
                          setOtpError('');
                          setPhoneNumber('');
                          setPhoneError('');
                          setOtpToken('');
                        }}
                        className="text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors"
                      >
                        <i className="fas fa-arrow-left mr-1"></i>
                        Back to login
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={resendTimer > 0 || isLoading}
                        className="text-sm text-sky-700 hover:text-sky-900 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleSendOtpEmail}
                      disabled={isLoading}
                      className="w-full text-sm text-center py-2 px-4 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-envelope mr-2"></i>
                      Send code by email
                    </button>
                  </div>
                </form>
              </div>
            ) : !showForgotPassword ? (
              <div>
                {/* Form Header */}
                <div className="text-center mb-8">
                  {/* Blinking logo (same style as Sidebar) */}
                  <div className="mx-auto mb-5 flex items-center justify-center">
                    <div className="relative group">
                      <div className="absolute -inset-2 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300 animate-pulse bg-indigo-600/40"></div>
                      <div className="relative w-20 h-20 rounded-3xl flex items-center justify-center shadow-xl ring-4 ring-offset-4 ring-offset-transparent ring-indigo-600/20 animate-pulse bg-white">
                        <img
                          src="/logo.png"
                          alt="Aura Group"
                          className="w-12 h-12 object-contain drop-shadow"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl items-center justify-center hidden shadow-md">
                          <i className="fas fa-database text-white text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-sm animate-pulse"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-black text-slate-900 mb-2 tracking-tight">
                    Sign in
                  </h3>
                </div>

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-5">
                    {/* Email Field */}
                    <div className="group">
                      <label className="block text-slate-700 text-sm font-semibold mb-2">
                        Email
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className="fas fa-envelope text-slate-400 group-focus-within:text-sky-600 transition-colors"></i>
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          autoComplete="username"
                          spellCheck={false}
                          className={`w-full pl-12 pr-4 py-3.5 bg-slate-50 rounded-2xl text-slate-900 placeholder-slate-400 ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition-all duration-200 ${
                            errors.email ? 'ring-red-300 focus:ring-red-500/30' : ''
                          }`}
                          placeholder="name@company.com"
                          aria-invalid={Boolean(errors.email)}
                        />
                      </div>
                      {errors.email && (
                        <p className="text-red-600 text-sm mt-2 flex items-center">
                          <i className="fas fa-exclamation-circle mr-2"></i>
                          {errors.email as string}
                        </p>
                      )}
                    </div>

                    {/* Password Field */}
                    <div className="group">
                      <label className="block text-slate-700 text-sm font-semibold mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className="fas fa-lock text-slate-400 group-focus-within:text-sky-600 transition-colors"></i>
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          autoComplete="current-password"
                          className={`w-full pl-12 pr-14 py-3.5 bg-slate-50 rounded-2xl text-slate-900 placeholder-slate-400 ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition-all duration-200 ${
                            errors.password ? 'ring-red-300 focus:ring-red-500/30' : ''
                          }`}
                          placeholder="Enter your password"
                          aria-invalid={Boolean(errors.password)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-red-600 text-sm mt-2 flex items-center">
                          <i className="fas fa-exclamation-circle mr-2"></i>
                          {errors.password as string}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Login Error Message */}
                  {loginError && (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-2">
                      <p className="text-red-700 text-sm font-semibold flex items-center">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        {loginError}
                      </p>
                      {otpToken && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowOtpStep(true);
                            setShowPhoneStep(false);
                            setLoginError('');
                          }}
                          className="text-sm text-sky-600 hover:text-sky-700 font-medium underline"
                        >
                          I received an OTP on WhatsApp — go to OTP entry
                        </button>
                      )}
                    </div>
                  )}

                  {/* Remember Me & Forgot Password */}
                  <div className="flex items-center justify-between">
                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="checkbox"
                        name="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleInputChange}
                        className="w-5 h-5 rounded-md border-slate-300 text-sky-600 focus:ring-sky-500 focus:ring-2 transition-all"
                      />
                      <span className="ml-3 text-slate-700 text-sm font-medium group-hover:text-slate-900 transition-colors">
                        Remember me
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-sky-700 hover:text-sky-900 font-semibold transition-colors relative"
                    >
                      Forgot password?
                    </button>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="relative w-full overflow-hidden bg-gradient-to-r from-sky-600 via-violet-600 to-sky-700 hover:brightness-110 text-white font-bold py-3.5 px-6 rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl"
                  >
                    <span className="absolute inset-0 opacity-0 transition-opacity duration-300 hover:opacity-100 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_45%)]" />
                    {isLoading ? (
                      <div className="relative flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                        Signing in…
                      </div>
                    ) : (
                      <div className="relative flex items-center justify-center">
                        <i className="fas fa-sign-in-alt mr-3"></i>
                        Sign in
                      </div>
                    )}
                  </button>
                </form>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                  <p className="text-slate-500 text-xs">
                    Having trouble? Contact your administrator.
                  </p>
                </div>
              </div>
            ) : (
              <ForgotPasswordForm
                onSubmit={handleForgotPassword}
                onBack={() => setShowForgotPassword(false)}
              />
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Forgot Password Component
interface ForgotPasswordFormProps {
  onSubmit: (email: string) => void;
  onBack: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSubmit, onBack }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    try {
      await onSubmit(email);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-8">
        {/* Blinking logo (same style as Sign in) */}
        <div className="mx-auto mb-5 flex items-center justify-center">
          <div className="relative group">
            <div className="absolute -inset-2 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300 animate-pulse bg-indigo-600/40"></div>
            <div className="relative w-20 h-20 rounded-3xl flex items-center justify-center shadow-xl ring-4 ring-offset-4 ring-offset-transparent ring-indigo-600/20 animate-pulse bg-white">
              <img
                src="/logo.png"
                alt="Aura Group"
                className="w-12 h-12 object-contain drop-shadow"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl items-center justify-center hidden shadow-md">
                <i className="fas fa-database text-white text-2xl"></i>
              </div>
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-sm animate-pulse"></div>
          </div>
        </div>
        <h3 className="text-2xl font-black text-slate-900 mb-2">Reset password</h3>
        <p className="text-slate-600">We’ll email you a secure reset link.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="group">
          <label className="block text-slate-700 text-sm font-semibold mb-2">Email</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <i className="fas fa-envelope text-slate-400 group-focus-within:text-sky-600 transition-colors"></i>
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              spellCheck={false}
              className="w-full pl-12 pr-4 py-3.5 bg-slate-50 rounded-2xl text-slate-900 placeholder-slate-400 ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition-all duration-200"
              placeholder="name@company.com"
              required
            />
          </div>
        </div>

        <div className="flex space-x-4">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 font-semibold py-3.5 px-6 rounded-2xl transition-all duration-200"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back
          </button>
          <button
            type="submit"
            disabled={isLoading || !email}
            className="flex-1 bg-gradient-to-r from-sky-600 to-violet-600 hover:brightness-110 text-white font-bold py-3.5 px-6 rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                Sending...
              </div>
            ) : (
              <>
                <i className="fas fa-paper-plane mr-2"></i>
                Send Reset Link
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// Reset Password Component
interface ResetPasswordFormProps {
  token: string;
  onSuccess: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ token, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordsMatch) return;

    setIsLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-8">
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-sky-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg text-white">
          <i className="fas fa-lock text-xl"></i>
        </div>
        <h3 className="text-2xl font-black text-slate-900 mb-2">Set a new password</h3>
        <p className="text-slate-600">Choose a strong password to secure your account.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="group">
          <label className="block text-slate-700 text-sm font-semibold mb-2">New password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <i className="fas fa-lock text-slate-400 group-focus-within:text-sky-600 transition-colors"></i>
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full pl-12 pr-14 py-3.5 bg-slate-50 rounded-2xl text-slate-900 placeholder-slate-400 ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition-all duration-200"
              placeholder="Enter new password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
          </div>
        </div>

        <div className="group">
          <label className="block text-slate-700 text-sm font-semibold mb-2">Confirm password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <i className="fas fa-lock text-slate-400 group-focus-within:text-sky-600 transition-colors"></i>
            </div>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full pl-12 pr-14 py-3.5 bg-slate-50 rounded-2xl text-slate-900 placeholder-slate-400 ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition-all duration-200"
              placeholder="Confirm new password"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
          </div>
          {!passwordsMatch && confirmPassword && (
            <p className="text-red-600 text-sm mt-2 flex items-center">
              <i className="fas fa-exclamation-circle mr-2"></i>
              Passwords do not match
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading || !passwordsMatch}
          className="w-full bg-gradient-to-r from-sky-600 to-violet-600 hover:brightness-110 text-white font-bold py-3.5 px-6 rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
              Resetting...
            </div>
          ) : (
            <>
              <i className="fas fa-check mr-2"></i>
              Reset Password
            </>
          )}
        </button>

        <button
          type="button"
          className="w-full border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 font-semibold py-3.5 px-6 rounded-2xl transition-all duration-200"
          onClick={() => window.location.href = '/login'}
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Back to Login
        </button>
      </form>
    </div>
  );
};

export default Login; 